﻿<template>
    <div>
        <ModalMessage
            :visible="showSuccessMessage"
            :title="successTitle"
            :messages="successMessages"
            @close="showSuccessMessage = false"
        />

        <div class="p-6 pb-0">
            <BreadCrumbs :navigation="navigation" />
        </div>

        <div class="p-6 pb-0">
            <div class="flex justify-between items-center mb-4">
                <h2 class="text-xl font-semibold text-gray-900">
                    {{ $t('at.lista_attivita.title') }}
                </h2>

                <button
                    v-if="canManageActivities"
                    @click="openAddActivityPanel"
                    class="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                    {{ $t('at.actions.new_activity') }}
                </button>
            </div>

            <PageServerTable
                :headers="headers"
                :items="items"
                :items-per-page.sync="ipp"
                :items-per-page-options="ippo"
                :sort-by="sortby"
                :items-length="count"
                :reload-fn="reload"
                v-model.selected="selected"
                :item-value="'Item'"
                :page="page"
                show-current-page
                @click:row="onRowClick"
            >
                <template #item.Item="{ item }">
                    <span>'{{ item.Item }}'</span>
                </template>

                <template #item.select="{ item }">
                    <div class="inline-flex items-center gap-1">
                        <button
                            v-if="canManageActivities"
                            @click.stop="openEditModal(item)"
                            type="button"
                            class="bg-blue-100 text-blue-700 rounded-full p-1.5 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                            :title="$t('at.actions.update') || 'Modifica'"
                        >
                            <svg class="w-4 h-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path d="M17.414 2.586a2 2 0 00-2.828 0L7 10.172V13h2.828l7.586-7.586a2 2 0 000-2.828z" />
                                <path fill-rule="evenodd" d="M2 6a2 2 0 012-2h4a1 1 0 010 2H4v10h10v-4a1 1 0 112 0v4a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" clip-rule="evenodd" />
                            </svg>
                        </button>

                        <button
                            v-if="canManageActivities"
                            @click.stop="openDeleteConfirm(item)"
                            type="button"
                            class="bg-red-100 text-red-700 rounded-full p-1.5 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                            :title="$t('at.actions.delete') || 'Elimina'"
                        >
                            <svg class="w-4 h-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                <path fill-rule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm4-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clip-rule="evenodd" />
                            </svg>
                        </button>
                    </div>
                </template>

                <template #item.start_date="{ item }">
                    <span>{{ item.start_date ? formatDate(item.start_date) : '-' }}</span>
                </template>

                <template #item.end_date="{ item }">
                    <span>{{ item.end_date ? formatDate(item.end_date) : '-' }}</span>
                </template>

                <template #item.status="{ item }">
                    <div class="flex flex-wrap items-center gap-1.5">
                        <span>{{ item.status || '-' }}</span>
                        <span
                            v-if="item.running_timer"
                            class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-semibold bg-green-500 text-white"
                            :title="'Timer avviato alle ' + formatTimerTime(item.running_timer.started_at)"
                        >
                            <i class="bi bi-play-fill"></i>
                            Timer
                        </span>
                        <span
                            v-if="item.activity_type === 'goods_receipt' && item.goods_receipt_status === 'waiting'"
                            class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700"
                        >
                            📦 In attesa merce
                        </span>
                        <span
                            v-if="item.activity_type === 'goods_receipt' && item.goods_receipt_status === 'received'"
                            class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700"
                        >
                            ✅ Merce ricevuta
                        </span>
                        <span
                            v-if="item.earliest_start_at"
                            class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200"
                            :title="'Pianificabile da: ' + formatDate(item.earliest_start_at)"
                        >
                            🕐 Da: {{ formatDate(item.earliest_start_at) }}
                        </span>
                    </div>
                </template>

                
            </PageServerTable>
        </div>

        <!-- Action Panel / Drawer unico -->
        <transition name="action-panel-fade">
            <div
                v-if="showActionsPanel"
                class="fixed inset-0 z-50 bg-gray-900 bg-opacity-40 mt-10"
                @click="closeActionsPanel"
            >
                <transition name="action-panel-slide">
                    <aside
                        v-if="showActionsPanel"
                        class="absolute right-0 mt-10 h-[calc(100vh-5rem)] w-full max-w-6xl bg-white shadow-2xl flex flex-col rounded-tl-xl"
                        @click.stop
                    >
                        <!-- Header -->
                        <div class="border-b border-gray-200 px-6 py-5 bg-white">
                            <div class="flex items-start justify-between gap-4">
                                <div class="min-w-0">
                                    <div class="flex flex-wrap items-center gap-2 mb-2">
                                        <span class="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                            Attività
                                        </span>

                                        <span
                                            v-if="currentStatus"
                                            class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                                            :class="statusBadgeClass(currentStatus)"
                                        >
                                            {{ getStatusLabel(currentStatus) }}
                                        </span>

                                        <span
                                            v-if="currentPriority"
                                            class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                                            :class="priorityBadgeClass(currentPriority)"
                                        >
                                            {{ $t('at.priority.' + currentPriority) }}
                                        </span>
                                    </div>

                                    <h2 class="text-xl font-semibold text-gray-900 truncate">
                                        {{ panelTitle }}
                                    </h2>

                                    <p v-if="panelSubtitle" class="text-sm text-gray-500 mt-1 truncate">
                                        {{ panelSubtitle }}
                                    </p>
                                </div>

                                <button
                                    type="button"
                                    @click="closeActionsPanel"
                                    class="rounded-full p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    :title="$t('at.actions.close')"
                                >
                                    <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        </div>

                        <!-- Tabs -->
                        <div class="border-b border-gray-200 px-6 bg-gray-50">
                            <div class="flex flex-wrap gap-2 py-3">
                                <button
                                    v-for="tab in availableTabs"
                                    :key="tab.key"
                                    type="button"
                                    @click="setActiveAction(tab.key)"
                                    class="px-3 py-2 rounded-md text-sm font-medium transition-colors"
                                    :class="activeAction === tab.key
                                        ? 'bg-blue-500 text-white shadow-sm'
                                        : 'text-gray-700 hover:bg-white hover:text-gray-900 border border-transparent hover:border-gray-200'"
                                >
                                    {{ tab.label }}
                                </button>
                            </div>
                        </div>

                        <!-- Body -->
                        <div class="flex-1 overflow-y-auto px-6 py-6 bg-white">
                            <!-- Details -->
                            <section v-if="activeAction === 'details'" class="space-y-4">
                                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div class="md:col-span-2">
                                        <label class="block text-gray-700 font-medium mb-1">{{ $t('at.fields.title') }}</label>
                                        <div class="p-3 bg-gray-50 border border-gray-200 rounded-md">
                                            {{ detailsData.title || '-' }}
                                        </div>
                                    </div>

                                    <div class="md:col-span-2">
                                        <label class="block text-gray-700 font-medium mb-1">{{ $t('at.fields.description') }}</label>
                                        <div class="p-3 bg-gray-50 border border-gray-200 rounded-md min-h-[80px] whitespace-pre-line">
                                            {{ detailsData.description || '-' }}
                                        </div>
                                    </div>

                                    <div>
                                        <label class="block text-gray-700 font-medium mb-1">{{ $t('at.fields.client') }}</label>
                                        <div class="p-3 bg-gray-50 border border-gray-200 rounded-md">
                                            {{ detailsData.client_description || '-' }}
                                        </div>
                                    </div>

                                    <div>
                                        <label class="block text-gray-700 font-medium mb-1">{{ $t('at.fields.job') }}</label>
                                        <div class="p-3 bg-gray-50 border border-gray-200 rounded-md">
                                            {{ detailsData.job_description || '-' }}
                                        </div>
                                    </div>

                                    <div>
                                        <label class="block text-gray-700 font-medium mb-1">{{ $t('at.fields.owner') }}</label>
                                        <div class="p-3 bg-gray-50 border border-gray-200 rounded-md">
                                            {{ detailsData.owner_description || '-' }}
                                        </div>
                                    </div>

                                    <div>
                                        <label class="block text-gray-700 font-medium mb-1">{{ $t('at.fields.priority') }}</label>
                                        <div class="p-3 bg-gray-50 border border-gray-200 rounded-md">
                                            <span :class="priorityTextClass(detailsData.priority)">
                                                {{ detailsData.priority ? $t('at.priority.' + detailsData.priority) : '-' }}
                                            </span>
                                        </div>
                                    </div>

                                    <div>
                                        <label class="block text-gray-700 font-medium mb-1">{{ $t('at.fields.start_date') }}</label>
                                        <div class="p-3 bg-gray-50 border border-gray-200 rounded-md">
                                            {{ detailsData.start_date ? formatDate(detailsData.start_date) : '-' }}
                                        </div>
                                    </div>

                                    <div>
                                        <label class="block text-gray-700 font-medium mb-1">{{ $t('at.fields.start_time') }}</label>
                                        <div class="p-3 bg-gray-50 border border-gray-200 rounded-md">
                                            {{ detailsData.start_time || '-' }}
                                        </div>
                                    </div>

                                    <div>
                                        <label class="block text-gray-700 font-medium mb-1">{{ $t('at.fields.end_date') }}</label>
                                        <div class="p-3 bg-gray-50 border border-gray-200 rounded-md">
                                            {{ detailsData.end_date ? formatDate(detailsData.end_date) : '-' }}
                                        </div>
                                    </div>

                                    <div>
                                        <label class="block text-gray-700 font-medium mb-1">{{ $t('at.fields.end_time') }}</label>
                                        <div class="p-3 bg-gray-50 border border-gray-200 rounded-md">
                                            {{ detailsData.end_time || '-' }}
                                        </div>
                                    </div>

                                    <div>
                                        <label class="block text-gray-700 font-medium mb-1">{{ $t('at.fields.delivery_date') }}</label>
                                        <div class="p-3 bg-gray-50 border border-gray-200 rounded-md">
                                            {{ detailsData.delivery_date ? formatDate(detailsData.delivery_date) : '-' }}
                                        </div>
                                    </div>

                                    <div>
                                        <label class="block text-gray-700 font-medium mb-1">{{ $t('at.fields.status') }}</label>
                                        <div class="p-3 bg-gray-50 border border-gray-200 rounded-md">
                                            <span :class="statusTextClass(detailsData.status)">
                                                {{ getStatusLabel(detailsData.status) }}
                                            </span>
                                        </div>
                                    </div>

                                    <div>
                                        <label class="block text-gray-700 font-medium mb-1">Ore di lavoro previste</label>
                                        <div class="p-3 bg-gray-50 border border-gray-200 rounded-md">
                                            {{ detailsData.total_hours ? detailsData.total_hours + 'h' : '-' }}
                                        </div>
                                    </div>

                                    <template v-if="detailsData.activity_type === 'goods_receipt'">
                                        <div>
                                            <label class="block text-gray-700 font-medium mb-1">Data stimata arrivo merce</label>
                                            <div class="p-3 bg-gray-50 border border-gray-200 rounded-md">
                                                {{ detailsData.goods_estimated_arrival_at ? formatDate(detailsData.goods_estimated_arrival_at) : '-' }}
                                            </div>
                                        </div>
                                        <div>
                                            <label class="block text-gray-700 font-medium mb-1">Stato ricezione</label>
                                            <div class="p-3 bg-gray-50 border border-gray-200 rounded-md">
                                                <span v-if="detailsData.goods_receipt_status === 'waiting'" class="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                                                    In attesa merce
                                                </span>
                                                <span v-else-if="detailsData.goods_receipt_status === 'received'" class="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                                                    Merce ricevuta
                                                </span>
                                                <span v-else>-</span>
                                            </div>
                                        </div>
                                        <div v-if="detailsData.goods_receipt_notes" class="md:col-span-2">
                                            <label class="block text-gray-700 font-medium mb-1">Note ricezione</label>
                                            <div class="p-3 bg-gray-50 border border-gray-200 rounded-md whitespace-pre-line">
                                                {{ detailsData.goods_receipt_notes }}
                                            </div>
                                        </div>
                                    </template>
                                </div>
                            </section>


                            <!-- Time registration -->
                            <section v-if="activeAction === 'time'">
                                <div v-if="selectedActivityForTime" class="mb-5 p-4 bg-gray-50 rounded-md border border-gray-200">
                                    <h3 class="font-medium text-gray-800">
                                        {{ selectedActivityForTime.title }}
                                    </h3>
                                    <p class="text-sm text-gray-600 mt-1">
                                        {{ selectedActivityForTime.description }}
                                    </p>
                                    <p class="text-sm text-gray-500 mt-2">
                                        {{ $t('at.lista_attivita.current_total_hours') }} {{ selectedActivityForTime.total_hours || 0 }}h
                                    </p>
                                </div>

                                <form id="time-registration-form" @submit.prevent="submitTimeRegistration">
                                    <div class="mb-4">
                                        <label for="work_date" class="block text-sm font-medium text-gray-700 mb-2">
                                            {{ $t('at.fields.work_date') }} *
                                        </label>
                                        <VueDatePicker
                                            v-model="timeForm.work_date"
                                            locale="it"
                                            format="dd/MM/yyyy"
                                            auto-apply
                                            :enable-time-picker="false"
                                            :teleport="true"
                                            :position="'bottom'"
                                            :max-date="today"
                                            class="w-full border border-gray-300 rounded-md shadow-sm"
                                            :placeholder="$t('at.lista_attivita.work_date_placeholder')"
                                        />
                                    </div>

                                    <div class="mb-4">
                                        <label for="hours" class="block text-sm font-medium text-gray-700 mb-2">
                                            {{ $t('at.fields.hours_worked') }} *
                                        </label>
                                        <input
                                            type="text"
                                            id="hours"
                                            v-model="timeForm.hours"
                                            placeholder="es. 1:30"
                                            pattern="^\d+:[0-5]\d$"
                                            autocomplete="off"
                                            class="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            :class="timeForm.hoursError ? 'border-red-400' : 'border-gray-300'"
                                            @input="timeForm.hoursError = ''"
                                            required
                                        />
                                        <p class="text-xs text-gray-400 mt-1">Formato ore:minuti — es. <strong>1:30</strong> = 1 ora e 30 minuti</p>
                                        <p v-if="timeForm.hoursError" class="text-red-500 text-sm mt-1">{{ timeForm.hoursError }}</p>
                                    </div>

                                    <div class="mb-6">
                                        <label for="description" class="block text-sm font-medium text-gray-700 mb-2">
                                            {{ $t('at.fields.work_description') }}
                                        </label>
                                        <textarea
                                            id="description"
                                            v-model="timeForm.description"
                                            rows="3"
                                            maxlength="1000"
                                            :placeholder="$t('at.lista_attivita.work_description_placeholder')"
                                            class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        ></textarea>
                                    </div>
                                </form>
                            </section>

                            <!-- Timer lavorazione -->
                            <section v-if="activeAction === 'timer'" class="max-w-xl space-y-5">
                                <div v-if="timerLoading" class="flex items-center gap-2 text-gray-500 text-sm">
                                    <i class="bi bi-hourglass-split animate-spin"></i>
                                    Caricamento...
                                </div>

                                <template v-else>
                                    <div v-if="runningTimer && runningTimer.activity_id !== selectedItem?.id" class="rounded-lg border border-orange-200 bg-orange-50 p-4 text-sm text-orange-800">
                                        <div class="flex items-start gap-2">
                                            <i class="bi bi-exclamation-triangle mt-0.5"></i>
                                            <p>Hai già una lavorazione attiva: <strong>{{ runningTimer.activity_title }}</strong>. Fermala prima di iniziarne un'altra.</p>
                                        </div>
                                    </div>

                                    <div v-else-if="runningTimer && runningTimer.activity_id === selectedItem?.id" class="space-y-4">
                                        <div class="rounded-lg border border-green-200 bg-green-50 p-4">
                                            <div class="flex items-center gap-2 mb-1">
                                                <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-green-500 text-white">
                                                    <i class="bi bi-play-fill"></i>
                                                    Timer attivo
                                                </span>
                                            </div>
                                            <p class="text-sm text-green-800 mt-1">
                                                Avviato alle <strong>{{ formatTimerTime(runningTimer.started_at) }}</strong>
                                            </p>
                                        </div>

                                        <div>
                                            <label class="block text-sm font-medium text-gray-700 mb-1">Note (opzionale)</label>
                                            <textarea
                                                v-model="timerDescription"
                                                rows="3"
                                                class="p93__input w-full"
                                                placeholder="Descrizione del lavoro svolto..."
                                            ></textarea>
                                        </div>

                                        <div class="flex flex-wrap gap-3">
                                            <button
                                                type="button"
                                                @click="stopTimer(selectedItem.id)"
                                                :disabled="timerLoading"
                                                class="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-md border border-red-300 text-red-700 hover:bg-red-50 disabled:opacity-50 transition-colors"
                                            >
                                                <i class="bi bi-stop-fill"></i>
                                                Pausa
                                            </button>
                                            <button
                                                type="button"
                                                @click="stopAndComplete(selectedItem.id)"
                                                :disabled="timerLoading"
                                                class="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-md bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 transition-colors"
                                            >
                                                <i class="bi bi-check2-circle"></i>
                                                Attività completata
                                            </button>
                                        </div>
                                    </div>

                                    <div v-else class="space-y-4">
                                        <p class="text-sm text-gray-600">Avvia il timer per registrare automaticamente le ore lavorate su questa attività.</p>
                                        <button
                                            type="button"
                                            @click="startTimer(selectedItem.id)"
                                            :disabled="timerLoading"
                                            class="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 transition-colors"
                                        >
                                            <i class="bi bi-play-fill"></i>
                                            Avvia lavorazione
                                        </button>
                                    </div>
                                </template>
                            </section>

                            <!-- Work details -->
                            <section v-if="activeAction === 'workDetails'">
                                <div v-if="selectedActivityForWorkDetails" class="mb-6 p-4 bg-gray-50 rounded-md border border-gray-200">
                                    <h3 class="font-medium text-gray-800 text-lg">
                                        {{ selectedActivityForWorkDetails.title }}
                                    </h3>
                                    <p class="text-sm text-gray-600 mt-1">
                                        {{ selectedActivityForWorkDetails.description }}
                                    </p>
                                    <div class="flex flex-wrap items-center gap-4 mt-2 text-sm text-gray-500">
                                        <span>{{ $t('at.lista_attivita.client_label') }} {{ selectedActivityForWorkDetails.company_name || '-' }}</span>
                                        <span>{{ $t('at.lista_attivita.total_hours_label') }} {{ selectedActivityForWorkDetails.total_hours || 0 }}h</span>
                                    </div>
                                </div>


                                <!-- Work progress dashboard -->
                                <div
                                    v-if="selectedActivityForWorkDetails"
                                    class="mb-6 grid grid-cols-1 md:grid-cols-4 gap-4"
                                >
                                    <div class="p-4 rounded-lg border border-gray-200 bg-white shadow-sm">
                                        <p class="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                            Ore previste
                                        </p>
                                        <p class="mt-2 text-2xl font-bold text-gray-900">
                                            {{ formatHours(workDetailsEstimatedHours) }}h
                                        </p>
                                    </div>

                                    <div class="p-4 rounded-lg border border-gray-200 bg-white shadow-sm">
                                        <p class="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                            Ore registrate
                                        </p>
                                        <p class="mt-2 text-2xl font-bold text-blue-700">
                                            {{ formatHours(workDetailsTotalHours) }}h
                                        </p>
                                    </div>

                                    <div class="p-4 rounded-lg border border-gray-200 bg-white shadow-sm">
                                        <p class="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                            Ore residue
                                        </p>
                                        <p
                                            class="mt-2 text-2xl font-bold"
                                            :class="workDetailsRemainingHours >= 0 ? 'text-green-700' : 'text-red-700'"
                                        >
                                            {{ formatHours(workDetailsRemainingHours) }}h
                                        </p>
                                    </div>

                                    <div class="p-4 rounded-lg border border-gray-200 bg-white shadow-sm">
                                        <p class="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                            Avanzamento
                                        </p>
                                        <p
                                            class="mt-2 text-2xl font-bold"
                                            :class="workDetailsProgressPercentage > 100 ? 'text-red-700' : 'text-gray-900'"
                                        >
                                            {{ workDetailsProgressPercentage }}%
                                        </p>
                                    </div>

                                    <div class="md:col-span-4 p-4 rounded-lg border border-gray-200 bg-gray-50">
                                        <div class="flex items-center justify-between mb-2">
                                            <span class="text-sm font-medium text-gray-700">
                                                Stato avanzamento lavorazione
                                            </span>
                                            <span
                                                class="text-sm font-semibold"
                                                :class="workDetailsProgressPercentage > 100 ? 'text-red-700' : 'text-blue-700'"
                                            >
                                                {{ workDetailsProgressPercentage }}%
                                            </span>
                                        </div>

                                        <div class="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
                                            <div
                                                class="h-3 rounded-full transition-all"
                                                :class="workDetailsProgressPercentage > 100 ? 'bg-red-500' : 'bg-blue-500'"
                                                :style="{ width: `${workDetailsProgressBarWidth}%` }"
                                            ></div>
                                        </div>

                                        <p class="mt-2 text-xs text-gray-500">
                                            Calcolato su ore registrate / ore di lavoro previste.
                                            <span v-if="workDetailsProgressPercentage > 100" class="font-semibold text-red-600">
                                                Attenzione: le ore registrate superano le ore previste.
                                            </span>
                                        </p>
                                    </div>
                                </div>

                                <div v-if="loadingWorkDetails" class="text-center py-8">
                                    <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                                    <p class="mt-2 text-gray-600">
                                        {{ $t('at.messages.loading_details') }}
                                    </p>
                                </div>

                                <div v-else-if="workDetails.length === 0" class="text-center py-8 text-gray-500">
                                    <p>{{ $t('at.messages.no_time_records') }}</p>
                                </div>

                                <div v-else class="space-y-4">
                                    <div class="overflow-x-auto border border-gray-200 rounded-md">
                                        <table class="min-w-full divide-y divide-gray-200">
                                            <thead class="bg-gray-50">
                                                <tr>
                                                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        {{ $t('at.fields.date') }}
                                                    </th>
                                                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        {{ $t('at.fields.user') }}
                                                    </th>
                                                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        {{ $t('at.fields.hours') }}
                                                    </th>
                                                    <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                        {{ $t('at.fields.description') }}
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody class="bg-white divide-y divide-gray-200">
                                                <tr v-for="workTime in workDetails" :key="workTime.id" class="hover:bg-gray-50">
                                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                        {{ formatDate(workTime.work_date) }}
                                                    </td>
                                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                        {{ workTime.user ? workTime.user.name : '-' }}
                                                    </td>
                                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                        <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                            {{ workTime.hours }}h
                                                        </span>
                                                    </td>
                                                    <td class="px-6 py-4 text-sm text-gray-900">
                                                        {{ workTime.description || '-' }}
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>

                                    <div class="mt-4 p-4 bg-blue-50 rounded-md border border-blue-100">
                                        <div class="flex justify-between items-center">
                                            <span class="font-medium text-blue-800">
                                                {{ $t('at.lista_attivita.total_registered_hours') }}
                                            </span>
                                            <span class="font-bold text-blue-900 text-lg">
                                                {{ workDetailsTotalHours }}h
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </section>

                        </div>

                        <!-- Footer -->
                        <div class="border-t border-gray-200 px-6 py-4 bg-gray-50">
                            <div class="flex flex-wrap justify-end items-center gap-3">
                                <button
                                    type="button"
                                    @click="closeActionsPanel"
                                    class="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600 transition-colors"
                                >
                                    {{ activeAction === 'time' ? $t('at.actions.cancel') : $t('at.actions.close') }}
                                </button>

                                <button
                                    v-if="activeAction === 'time'"
                                    type="submit"
                                    form="time-registration-form"
                                    class="bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    :disabled="isSubmittingTime"
                                >
                                    <span v-if="isSubmittingTime">{{ $t('at.messages.registering') }}</span>
                                    <span v-else>{{ $t('at.actions.register_time') }}</span>
                                </button>
                            </div>
                        </div>
                    </aside>
                </transition>
            </div>
        </transition>

        <!-- Modale creazione/modifica attività -->
        <ModaleAttivita
            v-model="showModaleAttivita"
            :attivita="editingAttivita"
            :all-attivita="[]"
            :departments="departments"
            :can-manage-activities="canManageActivities"
            @saved="onModaleAttivitaSaved"
            @close="onModaleAttivitaClose"
        />
    </div>
</template>

<script>
import { Link, router } from '@inertiajs/vue3';
import PageServerTable from '@/Components/PageServerTable.vue';
import BreadCrumbs from '@/Components/BreadCrumbs.vue';
import ModalMessage from '@/Components/ModalMessage.vue';
import ModaleAttivita from '@/Pages/Modules/attivita/Attivita/ModaleAttivita.vue';
import { useLoader } from '@/Composables/useLoader.js';
import axios from 'axios';

export default {
    props: ['errors', 'items', 'users', 'roles', 'count', 'ippo', 'workingHoursConfig', 'departments', 'isManager'],
    components: {
        Link,
        PageServerTable,
        BreadCrumbs,
        ModaleAttivita,
        ModalMessage
    },
    setup() {
        const { withInertiaLoader } = useLoader();
        return { withInertiaLoader };
    },
    data() {
        return {
            search: this.filters?.search || '',
            inventoryrows: [],
            selected: [],
            headers: [],
            sortby: [],
            ipp: 10,
            page: 1,
            isLoading: false,

            showSuccessMessage: false,
            successTitle: '',
            successMessages: [],

            showActionsPanel: false,
            activeAction: 'details',
            selectedItem: null,

            showModaleAttivita: false,
            editingAttivita: null,

            runningTimer: null,
            timerDescription: '',
            timerLoading: false,

            selectedActivityForTime: null,
            selectedActivityForWorkDetails: null,
            workDetails: [],
            workDetailsTotalHours: 0,
            loadingWorkDetails: false,
            isSubmittingTime: false,
            timeForm: {
                work_date: '',
                hours: '',
                hoursError: '',
                description: ''
            },

            detailsData: {},
        };
    },
    computed: {
        canManageActivities() {
            return this.$page.props.isAdminOrSuperAdmin || this.isManager;
        },
        today() {
            return new Date();
        },
        todayString() {
            return new Date().toISOString().split('T')[0];
        },
        navigation() {
            return [
                { name: this.$t('ui.dashboard'), href: route('dashboard') },
                { name: this.$t('modules.attivita'), href: route('modules.home', 'attivita') },
                { name: this.$t('at.breadcrumb.lista_attivita'), href: route('modules.attivita.lista-attivita') }
            ];
        },
        availableTabs() {
            return [
                { key: 'details', label: this.$t('at.actions.details') || 'Dettagli' },
                { key: 'timer', label: 'Timer' },
                { key: 'time', label: this.$t('at.actions.register_time') || 'Registra tempo' },
                { key: 'workDetails', label: this.$t('at.lista_attivita.work_details_modal_title') || 'Ore lavorate' }
            ];
        },
        panelTitle() {
            if (this.activeAction === 'time') {
                return this.$t('at.lista_attivita.time_modal_title');
            }

            if (this.activeAction === 'workDetails') {
                return this.$t('at.lista_attivita.work_details_modal_title');
            }

            return this.selectedItem?.title || this.$t('at.lista_attivita.details_modal_title');
        },
        panelSubtitle() {
            if (!this.selectedItem) return '';

            const chunks = [];
            if (this.selectedItem.company_name) chunks.push(this.selectedItem.company_name);
            if (this.selectedItem.job) chunks.push(this.selectedItem.job);

            return chunks.join(' · ');
        },
        currentStatus() {
            return this.selectedItem?.status || this.detailsData.status || '';
        },
        currentPriority() {
            return this.selectedItem?.priority || this.detailsData.priority || '';
        },
        workDetailsEstimatedHours() {
            return Number(this.selectedActivityForWorkDetails?.total_hours || 0);
        },
        workDetailsRemainingHours() {
            return Number((this.workDetailsEstimatedHours - Number(this.workDetailsTotalHours || 0)).toFixed(2));
        },
        workDetailsProgressPercentage() {
            if (!this.workDetailsEstimatedHours) {
                return 0;
            }

            return Math.round((Number(this.workDetailsTotalHours || 0) / this.workDetailsEstimatedHours) * 100);
        },
        workDetailsProgressBarWidth() {
            return Math.min(this.workDetailsProgressPercentage, 100);
        }
    },
    mounted() {
        this.loadRunningTimer();
    },
    methods: {
        async loadRunningTimer() {
            try {
                const res = await axios.get(route('modules.attivita.timer.current'));
                this.runningTimer = res.data.timer || null;
            } catch (e) {
                this.runningTimer = null;
            }
        },

        async startTimer(activityId) {
            this.timerLoading = true;
            try {
                const res = await axios.post(route('modules.attivita.timer.start', activityId));
                this.runningTimer = res.data.timer;
                this.showSuccess('Timer avviato.', [res.data.message]);
            } catch (e) {
                this.showSuccess('Errore', [e.response?.data?.message || 'Errore avvio timer.']);
            } finally {
                this.timerLoading = false;
            }
        },

        async stopTimer(activityId) {
            this.timerLoading = true;
            try {
                const res = await axios.post(route('modules.attivita.timer.stop', activityId), {
                    description: this.timerDescription,
                });
                this.runningTimer = null;
                this.timerDescription = '';
                this.showSuccess('Tempo registrato.', [res.data.message]);
            } catch (e) {
                this.showSuccess('Errore', [e.response?.data?.message || 'Errore stop timer.']);
            } finally {
                this.timerLoading = false;
            }
        },

        async stopAndComplete(activityId) {
            this.timerLoading = true;
            try {
                const res = await axios.post(route('modules.attivita.timer.stop-complete', activityId), {
                    description: this.timerDescription,
                });
                this.runningTimer = null;
                this.timerDescription = '';
                this.showSuccess('Completata.', [res.data.message]);
                this.reload();
            } catch (e) {
                this.showSuccess('Errore', [e.response?.data?.message || 'Errore stop e completa.']);
            } finally {
                this.timerLoading = false;
            }
        },

        formatTimerTime(isoString) {
            if (!isoString) return '—';
            const d = new Date(isoString);
            return d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
        },

        showSuccess(title, messages) {
            this.successTitle = title;
            this.successMessages = messages;
            this.showSuccessMessage = true;
        },

        reload(sortby = undefined, itemsperpage = undefined, page = undefined, afterdeletion = false) {
            if (sortby) this.sortby = sortby;
            if (itemsperpage) this.ipp = itemsperpage;
            if (page) this.page = page;

            router.post(
                window.location.href,
                {
                    headers: this.headers,
                    sortby: this.sortby,
                    itemsperpage: this.ipp,
                    page: this.page
                },
                { only: ['items', 'count'] }
            );
        },

        onRowClick(event, { item }) {
            this.openActionsPanel(item);
        },

        openActionsPanel(item) {
            this.selectedItem = item;
            this.prepareDetailsData(item);
            this.activeAction = 'details';
            this.showActionsPanel = true;
        },

        openAddActivityPanel() {
            this.editingAttivita = null;
            this.showModaleAttivita = true;
        },

        openEditModal(item) {
            this.editingAttivita = item;
            this.showModaleAttivita = true;
        },

        onModaleAttivitaSaved({ isEdit } = {}) {
            this.successTitle = this.$t('at.messages.success');
            this.successMessages = [isEdit ? this.$t('at.messages.activity_updated') : this.$t('at.messages.activity_created')];
            this.showSuccessMessage = true;
            if (this.selectedItem && this.editingAttivita && this.selectedItem.id === this.editingAttivita.id) {
                this.closeActionsPanel();
            }
            this.reload();
        },

        onModaleAttivitaClose() {
            this.editingAttivita = null;
        },

        async openDeleteConfirm(item) {
            if (!confirm(this.$t('at.lista_attivita.delete_confirm_text') + '\n\n' + item.title)) {
                return;
            }
            await this.deleteActivity(item);
        },

        async deleteActivity(item) {
            try {
                await this.withInertiaLoader(
                    router,
                    route('modules.attivita.elimina-attivita', { id: item.id }),
                    {
                        onSuccess: () => {
                            this.successTitle = this.$t('at.messages.success');
                            this.successMessages = [this.$t('at.messages.activity_deleted')];
                            this.showSuccessMessage = true;
                            if (this.selectedItem?.id === item.id) {
                                this.closeActionsPanel();
                            }
                            this.reload(undefined, undefined, undefined, true);
                        },
                        onError: () => {
                            this.successTitle = this.$t('at.messages.error');
                            this.successMessages = [this.$t('at.messages.error_delete')];
                            this.showSuccessMessage = true;
                        }
                    },
                    this.$t('at.messages.deleting'),
                    'delete'
                );
            } catch (error) {
                console.error('Errore durante l\'eliminazione:', error);
            }
        },

        closeActionsPanel() {
            this.showActionsPanel = false;
            this.activeAction = 'details';
            this.selectedItem = null;
            this.selectedActivityForTime = null;
            this.selectedActivityForWorkDetails = null;
            this.workDetails = [];
            this.workDetailsTotalHours = 0;
            this.loadingWorkDetails = false;
            this.timeForm.work_date = '';
            this.timeForm.hours = '';
            this.timeForm.description = '';
        },

        setActiveAction(action) {
            this.activeAction = action;

            if (action === 'details' && this.selectedItem) {
                this.prepareDetailsData(this.selectedItem);
            }

            if (action === 'timer') {
                this.timerDescription = '';
                this.loadRunningTimer();
            }

            if (action === 'time' && this.selectedItem) {
                this.prepareTimeRegistration(this.selectedItem);
            }

            if (action === 'workDetails' && this.selectedItem) {
                this.loadWorkDetails(this.selectedItem);
            }
        },

        prepareDetailsData(item) {
            this.detailsData = {
                title: item.title,
                description: item.description,
                client_description: item.company_name || '',
                job_description: item.job || '',
                owner_description: item.owner ? item.owner.name : '',
                start_date: item.start_date,
                start_time:item.start_time || '',
                end_date: item.end_date,
                end_time: item.end_time || '',
                delivery_date: item.delivery_date,
                priority: item.priority || 'media',
                status: item.status || 'nuova',
                total_hours: item.total_hours || 0,
                activity_type: item.activity_type || 'standard',
                goods_estimated_arrival_at: item.goods_estimated_arrival_at || null,
                goods_receipt_status: item.goods_receipt_status || null,
                goods_receipt_notes: item.goods_receipt_notes || null,
            };
        },

        prepareTimeRegistration(item) {
            this.selectedActivityForTime = item;
            this.timeForm.work_date = this.today;
            this.timeForm.hours = '';
            this.timeForm.hoursError = '';
            this.timeForm.description = '';
        },

        loadWorkDetails(item) {
            this.selectedActivityForWorkDetails = item;
            this.workDetails = [];
            this.workDetailsTotalHours = 0;
            this.loadingWorkDetails = true;

            axios.get(route('modules.attivita.tempi-lavoro', item.id))
                .then((response) => {
                    if (response.data) {
                        this.workDetails = response.data.work_times || [];
                        this.workDetailsTotalHours = response.data.total_worked_hours || 0;
                    }
                })
                .catch((error) => {
                    console.error('Errore nel caricamento dei dettagli lavorazione:', error);
                    this.successTitle = this.$t('at.messages.error');
                    this.successMessages = [this.$t('at.messages.loading_details')];
                    this.showSuccessMessage = true;
                })
                .finally(() => {
                    this.loadingWorkDetails = false;
                });
        },

        async submitTimeRegistration() {
            if (!this.selectedActivityForTime || this.isSubmittingTime) {
                return;
            }

            const parsedHours = this.parseHoursInput(this.timeForm.hours);
            if (parsedHours === null) {
                this.timeForm.hoursError = 'Formato non valido. Usa H:MM (es. 1:30)';
                return;
            }

            this.isSubmittingTime = true;

            try {
                const workDate = this.timeForm.work_date instanceof Date
                    ? this.timeForm.work_date.toISOString().split('T')[0]
                    : this.timeForm.work_date;

                const response = await axios.post(route('modules.attivita.registra-tempo'), {
                    activity_id: this.selectedActivityForTime.id,
                    work_date: workDate,
                    hours: parsedHours,
                    description: this.timeForm.description
                });

                if (response.data.success) {
                    this.successTitle = this.$t('at.messages.time_registered_title');
                    this.successMessages = [
                        `${this.timeForm.hours} - ${this.selectedActivityForTime.title}`
                    ];
                    this.showSuccessMessage = true;
                    this.closeActionsPanel();
                    this.reload();
                }
            } catch (error) {
                console.error('Errore nella registrazione del tempo:', error);

                if (error.response && error.response.data && error.response.data.errors) {
                    const errors = error.response.data.errors;
                    let errorMessages = [];

                    for (const field in errors) {
                        errorMessages = errorMessages.concat(errors[field]);
                    }

                    this.successTitle = this.$t('at.messages.error');
                    this.successMessages = errorMessages;
                    this.showSuccessMessage = true;
                }
            } finally {
                this.isSubmittingTime = false;
            }
        },

        parseHoursInput(value) {
            if (!value) return null;
            const match = value.trim().match(/^(\d+):([0-5]\d)$/);
            if (!match) return null;
            const h = parseInt(match[1], 10);
            const m = parseInt(match[2], 10);
            if (h === 0 && m === 0) return null;
            return parseFloat((h + m / 60).toFixed(4));
        },

        formatHours(value) {
            const number = Number(value || 0);

            if (Number.isInteger(number)) {
                return number;
            }

            return number.toFixed(2).replace(/\.00$/, '');
        },

        formatDate(date) {
            if (!date) return '-';
            const d = new Date(date);
            return d.toLocaleDateString('it-IT');
        },

        getStatusLabel(status) {
            if (!status) return '-';
            return this.$t('at.status.' + status);
        },

        statusTextClass(status) {
            return {
                'text-gray-600': status === 'nuova',
                'text-blue-600': status === 'assegnata',
                'text-purple-600': status === 'schedulata',
                'text-orange-600': status === 'in_lavorazione',
                'text-green-600': status === 'completata'
            };
        },

        priorityTextClass(priority) {
            return {
                'text-green-600': priority === 'bassa',
                'text-yellow-600': priority === 'media',
                'text-orange-600': priority === 'alta',
                'text-red-600': priority === 'critica'
            };
        },

        statusBadgeClass(status) {
            return {
                'bg-gray-100 text-gray-700': status === 'nuova',
                'bg-blue-100 text-blue-700': status === 'assegnata',
                'bg-purple-100 text-purple-700': status === 'schedulata',
                'bg-orange-100 text-orange-700': status === 'in_lavorazione',
                'bg-green-100 text-green-700': status === 'completata'
            };
        },

        priorityBadgeClass(priority) {
            return {
                'bg-green-100 text-green-700': priority === 'bassa',
                'bg-yellow-100 text-yellow-700': priority === 'media',
                'bg-orange-100 text-orange-700': priority === 'alta',
                'bg-red-100 text-red-700': priority === 'critica'
            };
        },

        getMinDateTime() {
            if (!this.workingHoursConfig) return '';
            const today = new Date();
            const [startHour, startMin] = (this.workingHoursConfig.morning_start || '08:30').split(':');
            today.setHours(parseInt(startHour));
            today.setMinutes(parseInt(startMin));
            return today.toISOString().slice(0, 16);
        },

        getMaxDateTime() {
            if (!this.workingHoursConfig) return '';
            const today = new Date();
            today.setDate(today.getDate() + 365);
            const [endHour, endMin] = (this.workingHoursConfig.afternoon_end || '17:30').split(':');
            today.setHours(parseInt(endHour));
            today.setMinutes(parseInt(endMin));
            return today.toISOString().slice(0, 16);
        },
    },
    mounted() {
        this.headers = this.$attrs.headers;
        this.ipp = this.$attrs.ipp;
        this.page = this.$attrs.page;
        this.sortby = this.$attrs.sortby;
    }
};
</script>

<style scoped>
.action-panel-fade-enter-active,
.action-panel-fade-leave-active {
    transition: opacity 0.18s ease;
}

.action-panel-fade-enter-from,
.action-panel-fade-leave-to {
    opacity: 0;
}

.action-panel-slide-enter-active,
.action-panel-slide-leave-active {
    transition: transform 0.22s ease;
}

.action-panel-slide-enter-from,
.action-panel-slide-leave-to {
    transform: translateX(100%);
}

.action-panel-slide-enter-to,
.action-panel-slide-leave-from {
    transform: translateX(0);
}
</style>
